import type { Route } from '@angular/router';
import { AppLayoutComponent } from './layout/app-layout.component';
import type { AdminUserRole } from '../backend/main';
import { unAuthGuard } from './auth/guards/un-auth.guard';
import { authGuard } from './auth/guards/auth.guard';
import { routes as authRoutes } from './auth/routes';
import { roleGuard } from './auth/guards/role/role.guard';
import { NotFoundComponent } from './not-found/not-found.component';
import { environment } from '../environment';
import { IconBrowserComponent } from './common/dev/icon-browser.component';
import { adminUserRoutes } from './feature/admin-user/admin-user.routes';
import { newsRoutes } from './feature/news/news.routes';
import { dirtyCheckCanDeactivateGuard } from './common/dirty-check/dirty-check';
import { AdminUserDtoService } from './feature/admin-user/service/admin-user-dto.service';
import { NewsDtoService } from './feature/news/service/news-dto.service';
import { NewsCategoryDtoService } from './feature/news/service/news-category-dto.service';
import { poiRoutes } from './feature/poi/poi.routes';
import { PoisDtoService } from './feature/poi/service/pois-dto.service';
import { locationsRoutes } from './feature/location/locations.routes';
import { LocationsDtoService } from './feature/location/service/locations-dto.service';
import { LocationsCategoryDtoService } from './feature/location/service/locations-category-dto.service';
import { eventRoutes } from './feature/event/event.routes';
import { EventsCategoryDtoService } from './feature/event/service/events-category-dto.service';
import { EventsDtoService } from './feature/event/service/events-dto.service';
import { offerRoutes } from './feature/offer/offer.routes';
import { OffersCategoryDtoService } from './feature/offer/service/offers-category-dto.service';
import { OffersDtoService } from './feature/offer/service/offers-dto.service';
import { notificationRoutes } from './feature/notification/notification.routes';
import { NotificationsDtoService } from './feature/notification/service/notifications-dto.service';
import { userRoutes } from './feature/user/user.routes';
import { tourRoutes } from './feature/tour/tour.routes';
import { TourDtoService } from './feature/tour/service/tour-dto.service';
import { onboardingStepRoutes } from './feature/onboarding-step/onboarding.routes';
import { OnboardingStepDtoService } from './feature/onboarding-step/service/onboarding-step-dto.service';

function i18nPrefix(name: string): string {
  return `PAGES.${name}.BREADCRUMB.LIST`;
}

export const routes: Route[] = [
  {
    path: '',
    pathMatch: 'full',
    redirectTo: '/news',
  },
  { path: '', canActivateChild: [unAuthGuard], children: authRoutes },
  {
    path: '',
    component: AppLayoutComponent,
    canActivateChild: [authGuard],
    children: [
      {
        path: 'admin-users',
        children: adminUserRoutes,
        title: i18nPrefix('ADMIN_USER'),
        canActivate: [roleGuard],
        data: {
          roles: ['SystemAdmin'] satisfies AdminUserRole[],
        },
        providers: [AdminUserDtoService],
      },
      {
        path: 'users',
        children: userRoutes,
        title: i18nPrefix('USER'),
        canActivate: [roleGuard],
        data: {
          roles: ['SystemAdmin', 'Admin'] satisfies AdminUserRole[],
        },
      },
      {
        path: 'news',
        children: newsRoutes,
        title: i18nPrefix('NEWS'),
        canActivate: [roleGuard],
        data: {
          roles: ['SystemAdmin', 'Admin'] satisfies AdminUserRole[],
        },
        providers: [NewsDtoService, NewsCategoryDtoService],
      },
      {
        path: 'locations',
        children: locationsRoutes,
        title: i18nPrefix('LOCATION'),
        canActivate: [roleGuard],
        providers: [LocationsDtoService, LocationsCategoryDtoService],
        data: {
          roles: ['SystemAdmin', 'Admin'] satisfies AdminUserRole[],
        },
      },
      {
        path: 'offers',
        children: offerRoutes,
        title: i18nPrefix('OFFER'),
        canActivate: [roleGuard],
        providers: [OffersDtoService, OffersCategoryDtoService],
        data: {
          roles: ['SystemAdmin', 'Admin'] satisfies AdminUserRole[],
        },
      },
      {
        path: 'events',
        children: eventRoutes,
        title: i18nPrefix('EVENT'),
        canActivate: [roleGuard],
        data: {
          roles: ['SystemAdmin', 'Admin'] satisfies AdminUserRole[],
        },
        providers: [EventsDtoService, EventsCategoryDtoService],
      },
      {
        path: 'pois',
        children: poiRoutes,
        title: i18nPrefix('POI'),
        canActivate: [roleGuard],
        data: {
          roles: ['SystemAdmin', 'Admin'] satisfies AdminUserRole[],
        },
        providers: [PoisDtoService],
      },
      {
        path: 'notifications',
        children: notificationRoutes,
        title: i18nPrefix('NOTIFICATIONS'),
        canActivate: [roleGuard],
        data: {
          roles: ['SystemAdmin', 'Admin'] satisfies AdminUserRole[],
        },
        providers: [NotificationsDtoService],
      },
      {
        path: 'tours',
        children: tourRoutes,
        title: i18nPrefix('TOUR'),
        canActivate: [roleGuard],
        data: {
          roles: ['SystemAdmin', 'Admin'] satisfies AdminUserRole[],
        },
        providers: [TourDtoService],
      },
      {
        path: 'onboarding-steps',
        children: onboardingStepRoutes,
        title: i18nPrefix('ONBOARDING_STEP'),
        canActivate: [roleGuard],
        data: {
          roles: ['SystemAdmin', 'Admin'] satisfies AdminUserRole[],
        },
        providers: [OnboardingStepDtoService],
      },
      {
        path: 'my-profile',
        loadComponent: () => import('./feature/my-profile/my-profile.component'),
        title: i18nPrefix('MY_PROFILE'),
        canActivate: [roleGuard],
        canDeactivate: [dirtyCheckCanDeactivateGuard('HasDirtyCheck')],
        data: {
          roles: ['SystemAdmin', 'Admin'] satisfies AdminUserRole[],
        },
      },
    ],
  },
  {
    path: '**',
    component: NotFoundComponent,
    title: 'NOT_FOUND_PAGE.TITLE',
  },
];

if (environment.isAngularDevMode) {
  routes.splice(1, 0, {
    path: 'icons',
    component: IconBrowserComponent,
    title: 'icon',
  });
}
